import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";

import { useSearchPatients } from "@elation/api-client/src/stitch-web";

import { useDebouncedValue } from "@src/shared/hooks/useDebouncedValue";
import { getHttpStatus } from "@src/shared/utils/http/errors";

import { PatientEntity } from "../../../../../domain";
import type { PatientSearchOption } from "../PatientSearch.types";

const SEARCH_DEBOUNCE_MS = 300;

interface UsePatientSearchOptionsParams {
  disabled: boolean;
  limit: number;
  query: string;
}

export function usePatientSearchOptions({
  disabled,
  limit,
  query,
}: UsePatientSearchOptionsParams) {
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const patientSearch = useSearchPatients(
    {
      q: debouncedQuery.trim() || undefined,
      ...PatientEntity.searchSortFor(debouncedQuery),
      limit,
    },
    {
      query: {
        enabled: !disabled,
        placeholderData: keepPreviousData,
        retry: (failureCount, err) => {
          const status = getHttpStatus(err);
          if (status != null && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  );
  const options = useMemo<PatientSearchOption[]>(
    () =>
      (patientSearch.data?.data.items ?? []).map((patient) => {
        const name = PatientEntity.displayName(patient) ?? "Unknown patient";

        return {
          key:
            PatientEntity.searchResultKey(patient) ??
            `patient-search:${name}:${patient.externalPatientId ?? "unknown"}`,
          patient,
          name,
          initials: PatientEntity.initials(patient) ?? "?",
          metadata: PatientEntity.listMetadata(patient) ?? "",
          helperText:
            patient.patientId == null
              ? `Will sync from ${PatientEntity.ehrLabel(patient) ?? "EHR"}`
              : null,
        };
      }),
    [patientSearch.data],
  );

  return {
    options,
    isError: patientSearch.isError,
    error: patientSearch.error,
    // With `placeholderData: keepPreviousData`, `isLoading` (pending && no
    // data) is only ever true on the very first fetch: every later fetch has
    // the prior page's data to show as a placeholder, so `isLoading` is
    // false even while a new request is in flight. We key off `isFetching`
    // instead so a genuinely-empty result set still shows a loading state
    // while the next query is in flight, but non-empty stale results stay
    // visible without a skeleton flash on every keystroke.
    isSearching: patientSearch.isFetching && options.length === 0,
    // Non-empty stale options intentionally stay on screen (see above) —
    // but they're still selectable and indistinguishable from a fresh
    // match unless something signals a refetch is in flight. Callers should
    // surface this (e.g. `aria-busy` + a dimmed list) without swapping the
    // options out for skeletons.
    isRefetching: patientSearch.isFetching && options.length > 0,
  } as const;
}
